class TestimonialCarousel {
  constructor(root) {
    this.root = root;
    this.viewport = root.querySelector('.tc-track-viewport');
    this.track = root.querySelector('.tc-track');
    if (!this.track || this.track.classList.contains('tc-track--grid')) return;

    this.slides = Array.from(this.track.querySelectorAll('.tc-slide'));
    this.prevBtn = root.querySelector('.tc-arrow--prev');
    this.nextBtn = root.querySelector('.tc-arrow--next');
    this.dotsWrap = root.closest('.tc-section').querySelector('.tc-dots');

    this.autoplay = root.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(root.dataset.autoplaySpeed, 10) || 5000;
    this.loop = root.dataset.loop === 'true';
    this.pauseOnHover = root.dataset.pauseOnHover === 'true';

    this.timer = null;

    this.buildDots();
    this.bindEvents();
    this.updateControls();
    if (this.autoplay) this.startAutoplay();
  }

  getSlidesPerView() {
    const w = window.innerWidth;
    const styles = getComputedStyle(this.root);
    if (w >= 990) return parseInt(styles.getPropertyValue('--tc-slides-desktop'), 10) || 3;
    if (w >= 750) return parseInt(styles.getPropertyValue('--tc-slides-tablet'), 10) || 2;
    return parseInt(styles.getPropertyValue('--tc-slides-mobile'), 10) || 1;
  }

  getPageCount() {
    const perView = this.getSlidesPerView();
    return Math.max(1, Math.ceil(this.slides.length / perView));
  }

  getCurrentPage() {
    const perView = this.getSlidesPerView();
    const slideWidth = this.slides[0].getBoundingClientRect().width + this.gapPx();
    const index = Math.round(this.track.scrollLeft / slideWidth);
    return Math.min(this.getPageCount() - 1, Math.round(index / perView));
  }

  gapPx() {
    const gap = getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap;
    return parseFloat(gap) || 0;
  }

  buildDots() {
    if (!this.dotsWrap) return;
    this.dotsWrap.innerHTML = '';
    const pageCount = this.getPageCount();
    for (let i = 0; i < pageCount; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'tc-dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', 'Go to slide group ' + (i + 1));
      dot.addEventListener('click', () => this.goToPage(i));
      this.dotsWrap.appendChild(dot);
    }
    this.dots = Array.from(this.dotsWrap.children);
    this.setActiveDot(0);
  }

  setActiveDot(page) {
    if (!this.dots) return;
    this.dots.forEach((d, i) => d.classList.toggle('is-active', i === page));
  }

  goToPage(page) {
    const perView = this.getSlidesPerView();
    const targetIndex = Math.min(this.slides.length - 1, page * perView);
    const target = this.slides[targetIndex];
    if (!target) return;
    this.track.scrollTo({ left: target.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
  }

  next() {
    const pageCount = this.getPageCount();
    const current = this.getCurrentPage();
    let nextPage = current + 1;
    if (nextPage >= pageCount) {
      if (!this.loop) { nextPage = pageCount - 1; }
      else { nextPage = 0; }
    }
    this.goToPage(nextPage);
  }

  prev() {
    const current = this.getCurrentPage();
    let prevPage = current - 1;
    if (prevPage < 0) {
      if (!this.loop) { prevPage = 0; }
      else { prevPage = this.getPageCount() - 1; }
    }
    this.goToPage(prevPage);
  }

  updateControls() {
    const current = this.getCurrentPage();
    const pageCount = this.getPageCount();
    this.setActiveDot(current);

    if (!this.loop) {
      if (this.prevBtn) this.prevBtn.disabled = current === 0;
      if (this.nextBtn) this.nextBtn.disabled = current === pageCount - 1;
    }
  }

  bindEvents() {
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => { this.next(); this.restartAutoplay(); });
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => { this.prev(); this.restartAutoplay(); });

    let scrollTimeout;
    this.track.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => this.updateControls(), 100);
    }, { passive: true });

    window.addEventListener('resize', () => {
      this.buildDots();
      this.updateControls();
    });

    if (this.pauseOnHover && this.autoplay) {
      this.root.addEventListener('mouseenter', () => this.stopAutoplay());
      this.root.addEventListener('mouseleave', () => this.startAutoplay());
    }
  }

  startAutoplay() {
    this.stopAutoplay();
    this.timer = setInterval(() => this.next(), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  restartAutoplay() {
    if (this.autoplay) this.startAutoplay();
  }
}

function initTestimonialCarousels(root = document) {
  root.querySelectorAll('.tc-section').forEach((section) => {
    if (section.dataset.tcInitialized) return;
    section.dataset.tcInitialized = 'true';
    new TestimonialCarousel(section);
  });
}

document.addEventListener('DOMContentLoaded', () => initTestimonialCarousels());
document.addEventListener('shopify:section:load', (e) => initTestimonialCarousels(e.target));
